import { createRouter, createWebHistory } from 'vue-router'
import { supabase } from '@/lib/supabaseClient'
import ContentView from '@/views/ContentView.vue'
import LoginView from '@/views/LoginView.vue'
import DashboardView from '../views/DashboardView.vue'
import EmailsView from '@/views/EmailsView.vue'
import RepliesView from '@/views/RepliesView.vue'
import ResetPasswordView from '@/views/ResetPasswordView.vue'
import FollowUpsView from '@/views/FollowUpsView.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Public Routes
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
      meta: { public: true }
    },

    // Public Citizen Campaigns Routes
    {
      path: '/campaigns',
      name: 'public-topics',
      component: () => import('../views/PublicTopicsView.vue'),
      meta: { public: true }
    },
    {
      path: '/kampanja/:slug',
      name: 'public-topic-detail',
      component: () => import('../views/PublicTopicDetailView.vue'),
      meta: { public: true }
    },
    {
      path: '/verifikacija/:token',
      name: 'public-verification',
      component: () => import('../views/PublicVerificationView.vue'),
      meta: { public: true }
    },

    // Public Briefings Routes
    // Hosted briefing pages are meant to be forwarded and read without an
    // account; the API serves published briefings only.
    {
      path: '/brifing/:slug',
      name: 'public-briefing',
      component: () => import('../views/PublicBriefingView.vue'),
      meta: { public: true }
    },

    // Admin Routes
    {
      path: '/admin',
      name: 'admin',
      component: () => import('../views/ContentView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/login',
      name: 'login',
      component: LoginView,
      meta: { public: true }
    },
    {
      path: '/admin/reset-password',
      name: 'reset-password',
      component: ResetPasswordView,
      meta: { public: true }
    },
    {
      path: '/admin/content',
      name: 'content',
      component: ContentView,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/prospecting',
      name: 'prospecting',
      component: () => import('../views/ProspectingView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/institutions',
      name: 'institutions',
      component: () => import('../views/InstitutionsView.vue').catch(() => {
        window.location.reload()
        return Promise.reject('Reloading to get latest assets')
      }),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/emails',
      name: 'emails',
      component: EmailsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/replies',
      name: 'replies',
      component: RepliesView,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/matters',
      name: 'matters',
      component: () => import('../views/MattersView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/outcomes',
      name: 'outcomes',
      component: () => import('../views/OutcomesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/foi',
      name: 'foi',
      component: () => import('../views/FoiView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/follow-ups',
      name: 'follow-ups',
      component: FollowUpsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/citizen-campaigns',
      name: 'citizen-campaigns',
      component: () => import('../views/CitizenCampaignsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      // ProfilesView existed but was never imported, routed or linked, so the page
      // was unreachable and its backend 500s went unnoticed for the life of the
      // project. Both are fixed together; a rebuilt component nobody can open is
      // not a rebuilt feature.
      path: '/admin/sender-profiles',
      name: 'sender-profiles',
      component: () => import('../views/ProfilesView.vue'),
      meta: { requiresAuth: true }
    },
    // Redirect unmatched admin routes to admin login
    {
      path: '/admin/:pathMatch(.*)*',
      redirect: '/admin/login'
    }
  ]
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  console.log('🛣️ Router navigation:', { to: to.path, name: to.name, from: from.path })
  
  const { data: { session } } = await supabase.auth.getSession()
  
  if (to.meta.requiresAuth && !session) {
    // If route requires auth and user is not logged in, redirect to login
    console.log('❌ No session, redirecting to login')
    next('/admin/login')
  } else if (to.name === 'login' && session) {
    // If user is already logged in and tries to access login page, redirect to admin
    console.log('✅ Already logged in, redirecting to content')
    next('/admin/content')
  } else {
    console.log('✅ Navigation allowed to:', to.path)
    next()
  }
})

// Add navigation error handling
router.onError((error) => {
  console.error('Router error:', error)
})

export default router 